import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_SCAFFOLD } from '../../../lib/constants';
import { TScaffold } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import { SCAFFOLD_STATUS_ENUM } from '../../../lib/constants/dbConstants';
import { ScaffoldStatusEnum } from '../../../graph-queries/__generated__/graphql';

const ScaffoldForm: FC<{
  defaultValues?: TScaffold;
  onSubmit: (scaffold: TScaffold) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      status: ScaffoldStatusEnum;
    };

    const scaffold: TScaffold = {
      ...EMPTY_SCAFFOLD,
      ...inputData,
      assetId: asset?.assetId || -1,
      width: parseInt(inputData.width),
      height: parseInt(inputData.height),
      length: parseInt(inputData.length),
      weight: parseInt(inputData.weight),
      erectHours: parseInt(`${inputData.erectHours}`),
      dismantleHours: parseInt(`${inputData.dismantleHours}`),
      outsideCover: inputData.outsideCover === 'Yes',
    };
    if (defaultValues?.scaffoldId) scaffold.scaffoldId = defaultValues.scaffoldId;

    onSubmit(scaffold)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="scaffold-plan-ref">Reference Number</Label>
          <Input
            type="text"
            name="planRef"
            id="scaffold-plan-ref"
            required
            defaultValue={defaultValues?.['planRef'] || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="scaffold-description">Scaffold Description</Label>
          <Input
            type="text"
            name="description"
            id="scaffold-description"
            required
            defaultValue={defaultValues?.['description'] || ''}
          />
        </FormGroup>
      </div>

      <div className="col-md-6">
        <FormGroup>
          <Label for="scaffold-weight">Weight of Scaffold (kg)</Label>
          <Input
            type="number"
            name="weight"
            id="scaffold-weight"
            defaultValue={defaultValues?.weight || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="scaffold-safe-working">Safe Working Limit (kg)</Label>
          <Input
            type="text"
            name="safeWorkingLimit"
            id="scaffold-safe-working"
            defaultValue={defaultValues?.safeWorkingLimit || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="scaffold-overside-cover">Overside Cover</Label>
          <Input
            type="select"
            name="outsideCover"
            id="scaffold-overside-cover"
            defaultValue={
              defaultValues?.outsideCover === true
                ? 'Yes'
                : defaultValues?.outsideCover === false
                  ? 'No'
                  : ''
            }
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            <option>Yes</option>
            <option>No</option>
          </Input>
        </FormGroup>
      </div>
      <div></div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-width">Width (m)</Label>
          <Input type="text" name="width" id="scaffold-width" defaultValue={defaultValues?.width} />
        </FormGroup>
      </div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-height">Height (m)</Label>
          <Input
            type="text"
            name="height"
            id="scaffold-height"
            defaultValue={defaultValues?.height}
          />
        </FormGroup>
      </div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-length">Length (m)</Label>
          <Input
            type="text"
            name="length"
            id="scaffold-length"
            defaultValue={defaultValues?.length}
          />
        </FormGroup>
      </div>

      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-erect">Erect Hours</Label>
          <Input
            type="text"
            name="erectHours"
            id="scaffold-erect"
            defaultValue={defaultValues?.erectHours || ''}
          />
        </FormGroup>
      </div>

      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-dismantle">Dismantle Hours</Label>
          <Input
            type="text"
            name="dismantleHours"
            id="scaffold-dismantle"
            defaultValue={defaultValues?.dismantleHours || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-team">Team Size</Label>
          <Input
            type="text"
            name="teamSize"
            id="scaffold-team"
            defaultValue={defaultValues?.teamSize || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="scaffold-status">Scaffold Status</Label>
          <Input
            type="select"
            name="status"
            id="scaffold-status"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            defaultValue={defaultValues?.scaffoldId || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(SCAFFOLD_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="scaffold-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="scaffold-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new scaffold
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default ScaffoldForm;
